import type { Store } from 'jotai/vanilla/store'
import { Quaternion, Vector3 } from '@/math'
import { registerSubjectProvider, type Subject, type Transform, subjectTransformKey } from './index'
import { uiAtom } from '@/uiState'
import type { Position } from '@/voxelMath'
import type { EventTuple } from '@/events/types'
import { makeShapeBuilder, type ShapeBuilderParams } from '@/builders/shapeBuilder'

const ID_CURRENT = 'current'
const IDENTITY_ROTATION = { x: 0, y: 0, z: 0, w: 1 }

function zeroPosition(): Position {
  return { x: 0, y: 0, z: 0 }
}

function getTransformFor(store: Store, id: string): Transform {
  const ui = store.get(uiAtom) as any
  const key = subjectTransformKey({ kind: 'builder', id })
  const t = (ui?.subjectTransforms?.[key]) || null
  return {
    translate: t?.translate ? { x: t.translate.x, y: t.translate.y, z: t.translate.z } : zeroPosition(),
    rotation: t?.rotation ? { x: t.rotation.x, y: t.rotation.y, z: t.rotation.z, w: t.rotation.w } : { ...IDENTITY_ROTATION },
  }
}

function setTransformFor(store: Store, id: string, next: Transform): void {
  const key = subjectTransformKey({ kind: 'builder', id })
  store.set(uiAtom, (prev) => {
    const st = { ...(prev.subjectTransforms || {}) }
    st[key] = { translate: { ...next.translate }, rotation: { ...next.rotation } }
    return { ...prev, subjectTransforms: st }
  })
}

function makeBuilderSubject(id: string, params: ShapeBuilderParams, store: Store): Subject {
  const getT = () => getTransformFor(store, id)
  const builderFor = (p = params, t = getT()) => makeShapeBuilder({ ...p, rotation: t.rotation }, t.translate)

  const subject: Subject = {
    kind: 'builder',
    id,
    center(): Position {
      const t = getT().translate
      const c = params.center
      return { x: c.x + t.x, y: c.y + t.y, z: c.z + t.z }
    },
    bbox() {
      return builderFor().bbox()
    },
    getTransform(): Transform {
      return getT()
    },
    setTransform(transform: Transform): void {
      setTransformFor(store, id, transform)
    },
    clampTransform(transform: Transform) {
      // No-op clamp for now; controller computes clamp when needed
      return transform
    },
    beginInteraction() {},
    endInteraction() {},
    previewEvents(): EventTuple[] {
      const evs = builderFor().previewEvents?.()
      return Array.isArray(evs) ? evs : []
    },
    commitTransform(): EventTuple[] {
      const evs = (builderFor() as any).toCommitEvents?.()
      return Array.isArray(evs) ? evs : []
    },
    onRotate(axis, deltaRad) {
      const t = getT()
      // Apply incremental rotation in world coordinates
      const q = new Quaternion(t.rotation.x, t.rotation.y, t.rotation.z, t.rotation.w)
      const axisVec = axis === 'x' ? new Vector3(1,0,0) : axis === 'y' ? new Vector3(0,1,0) : new Vector3(0,0,1)
      const dq = new Quaternion().setFromAxisAngle(axisVec.normalize(), deltaRad)
      const next = dq.multiply(q).normalize()
      setTransformFor(store, id, { translate: { ...t.translate }, rotation: { x: next.x, y: next.y, z: next.z, w: next.w } })
      return subject
    },
  }

  return subject
}

registerSubjectProvider('builder', (id, store) => {
  if (id !== ID_CURRENT) return null
  const ui = store.get(uiAtom) as any
  const bs = ui?.builderSubject
  if (!bs || bs.id !== id || !bs.params) return null
  return makeBuilderSubject(id, bs.params as ShapeBuilderParams, store)
})

export const builderSubjectId = ID_CURRENT
